import * as cookie from 'cookie';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import React from 'react';

import decryptToken from '@/components/shared/crypto/decryptToken';

import { generateUrlForImage } from '../../books/[bookId]';
import PDFViewer from '../../../components/PdfReader';
import { Endpoints } from '../../../network';
import {
  getBookRecourses,
  getLandingPageSeoSSR,
} from '../../../services/LandingPage';

export interface BookPdfProps {
  metaData: {
    title: string;
    description: string;
    keywords: string;
    image: string;
    openGraph: {
      title: string;
      description: string;
      image: string;
    };
    resource: string | null;
    pdfStatus: 'COMPLETED' | 'PENDING' | 'FAILED';
    jobId: string | null;
  } | null;
}
const Pdf: React.FC<BookPdfProps> = ({ metaData }) => {


  return (
    <>
      <Head>
        <title key='title'>
          {metaData?.title
            ? `Pdf Reader • ${metaData?.title}`
            : 'Pdf Reader - ZebraLearn'}
        </title>
        <meta
          name='description'
          content={
            metaData?.description || 'Explore our latest books at ZebraLearn.'
          }
          key='description'
        />
        <meta
          name='keywords'
          content={metaData?.keywords || 'books, learning, education'}
          key='keywords'
        />

        <meta
          property='og:title'
          content={
            metaData?.openGraph?.title || metaData?.title || 'ZebraLearn'
          }
          key='og-title'
        />
        <meta
          property='og:description'
          content={
            metaData?.openGraph?.description || metaData?.description || ''
          }
          key='og-description'
        />
        <meta
          property='og:image'
          content={
            metaData?.openGraph?.image ||
            'https://zebralearn-store.s3.ap-south-1.amazonaws.com/thubnail-creative.png'
          }
          key='og-image'
        />

        <meta
          property='og:image:type'
          content='image/webp'
          key='og-image-type-webp'
        />
        <meta
          property='og:image:type'
          content='image/png'
          key='og-image-type-png'
        />

        <meta
          name='twitter:title'
          content={
            metaData?.openGraph?.title || metaData?.title || 'ZebraLearn'
          }
          key='twitter-title'
        />
        <meta
          name='twitter:description'
          content={
            metaData?.openGraph?.description || metaData?.description || ''
          }
          key='twitter-description'
        />
        <meta
          name='twitter:image'
          content={
            metaData?.openGraph?.image ||
            'https://zebralearn-store.s3.ap-south-1.amazonaws.com/OG+1.png?tr=f-png'
          }
          key='twitter-image'
        />
        <meta
          property='article:section'
          content='Website Section'
          key='article-section'
        />
        <meta
          property='article:tag'
          content={metaData?.keywords as string}
          key='article-tag'
        />
      </Head>

      <div className='relative overflow-hidden h-max'>
        <span className='no-select h-max'>
          <PDFViewer
            resource={metaData?.resource as string}
            jobId={metaData?.jobId as string}
            pdfStatus={
              metaData?.pdfStatus as 'COMPLETED' | 'PENDING' | 'FAILED'
            }
          />
        </span>
      </div>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<BookPdfProps> = async (
  context: GetServerSidePropsContext,
) => {
  try {
    const { book_id } = context.query;
    const rawCookies = context.req.headers.cookie || '';
    const parsedCookies = cookie.parse(rawCookies || '');
    const token = parsedCookies['auth_token']?.trim();

    const metaData = await getLandingPageSeoSSR(book_id as string);

    let resourceResponse = null;
    try {
      resourceResponse = await getBookRecourses(
        book_id as string,
        token as string,
      );
    } catch (error) {
      resourceResponse = null;
    }

    let bookResource: string | null = null;
    let pdfStatus: 'COMPLETED' | 'PENDING' | 'FAILED' = 'PENDING';
    let jobId: string | undefined;
    bookResource = decryptToken(
      resourceResponse.licenseKey,
      process.env.PDF_SECRET_KEY as string,
    );

    if (
      resourceResponse?.status === 'COMPLETED' ||
      resourceResponse?.filePath
    ) {
      pdfStatus = 'COMPLETED';
    } else if (resourceResponse?.status === 'FAILED') {
      pdfStatus = 'FAILED';
    } else {
      pdfStatus = 'PENDING';
      jobId = resourceResponse.jobId;
    }

    const formattedMetaData = {
      title: metaData?.title || '',
      description: metaData?.description || '',
      image:
        generateUrlForImage(
          `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
          `${Endpoints.getCoverImagesForBooks}/`,
          `${book_id}`,
        ) || '',
      keywords: metaData?.keywords || '',
      openGraph: {
        title: metaData?.openGraph?.title ?? null,
        description: metaData?.openGraph?.description ?? null,
        image: metaData?.openGraph?.image ?? null,
      },
      resource: bookResource || null,
      pdfStatus,
      jobId: jobId || null,
    };

    return {
      props: {
        metaData: formattedMetaData,
      },
    };
  } catch (error) {
    console.error('Error in getServerSideProps:', error);
    return {
      props: {
        metaData: null,
      },
    };
  }
};

export default Pdf;
